import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { authApi } from '../api/auth';
import Input from '../components/ui/Input';
import Logo from '../components/shared/Logo';

const requestSchema = z.object({
  email: z.string().email('Bitte geben Sie eine gültige E-Mail-Adresse ein')
});

const resetSchema = z.object({
  newPassword: z.string()
    .min(8, 'Mindestens 8 Zeichen')
    .regex(/[A-Z]/, 'Mindestens ein Großbuchstabe')
    .regex(/[0-9]/, 'Mindestens eine Zahl'),
  confirmPassword: z.string().min(1, 'Bitte Passwort bestätigen')
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwörter stimmen nicht überein',
  path: ['confirmPassword']
});

function RequestForm() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(requestSchema)
  });

  const onSubmit = async (values) => {
    setError('');
    setLoading(true);
    try {
      await authApi.requestPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Anfrage fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center space-y-4">
        <CheckCircle className="w-12 h-12 text-teal-500 mx-auto" />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">E-Mail gesendet</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Falls ein Account mit dieser E-Mail existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen.
        </p>
        <Link to="/login" className="text-teal-600 hover:text-teal-700 text-sm font-medium">
          Zurück zur Anmeldung
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-slate-400">
        Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Input
        label="E-Mail"
        type="email"
        autoComplete="email"
        error={errors.email?.message}
        {...register('email')}
      />

      <button type="submit" disabled={loading} className="btn-metallic w-full">
        {loading ? 'Sende...' : 'Reset-Link senden'}
      </button>

      <div className="text-center">
        <Link to="/login" className="text-teal-600 hover:text-teal-700 text-sm">
          Zurück zur Anmeldung
        </Link>
      </div>
    </form>
  );
}

function ResetForm({ token }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(resetSchema)
  });

  const onSubmit = async (values) => {
    setError('');
    setLoading(true);
    try {
      await authApi.confirmPasswordReset(token, values.newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Zurücksetzen fehlgeschlagen. Der Link ist möglicherweise abgelaufen.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center space-y-4">
        <CheckCircle className="w-12 h-12 text-teal-500 mx-auto" />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Passwort zurückgesetzt</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.
        </p>
        <Link to="/login" className="inline-block btn-metallic px-6 py-2">
          Zur Anmeldung
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-slate-400">
        Geben Sie Ihr neues Passwort ein.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Input
        label="Neues Passwort"
        type="password"
        autoComplete="new-password"
        error={errors.newPassword?.message}
        {...register('newPassword')}
      />

      <Input
        label="Passwort bestätigen"
        type="password"
        autoComplete="new-password"
        error={errors.confirmPassword?.message}
        {...register('confirmPassword')}
      />

      <button type="submit" disabled={loading} className="btn-metallic w-full">
        {loading ? 'Speichere...' : 'Passwort zurücksetzen'}
      </button>
    </form>
  );
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 dark:bg-slate-950 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="xl" />
          </div>
          <p className="text-gray-500 dark:text-slate-400 mt-2">
            {token ? 'Neues Passwort festlegen' : 'Passwort zurücksetzen'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700">
          {token ? <ResetForm token={token} /> : <RequestForm />}
        </div>
      </div>
    </div>
  );
}
